import React, { useState, useCallback, useEffect, useRef, lazy, Suspense } from 'react';
import { v4 as uuidV4 } from 'uuid';
import useAssistant from 'hooks/useAssistant';
import DefaultModal from 'components/Utils/DefaultModal';
import DefaultButton from 'components/Utils/NewDefaultButton';
import DefaultChip from 'components/Utils/DefaultChip';
import DefaultTextField from 'components/Utils/DefaultTextField';
import Icon from 'components/Utils/NewIcon';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import { useIsLoading } from 'careand-redux/slices/ui/hooks';
import MiscUtils from 'services/MiscUtils';
import { fetchReportCss, variantFromPromptName, type ReportVariant } from 'services/ReportCssUtils';
import type { Patient } from 'generated/api';
import type { PromptName } from 'thunks/runSavedPromptTyped';

const RichTextEditable = lazy(() => import('components/Utils/RichTextEditable'));

interface GenerateReportModalProps {
  patient: Patient;
  onClose: () => void;
}

// Whitelisted prompts that can be used for report generation
const REPORT_PROMPTS: PromptName[] = [
  'Menopause - Executive Report',
  'Peak Performance - Executive Report',
  'Menopause - Provider Report',
];

// Prompts that output HTML instead of markdown
const HTML_REPORT_PROMPTS = new Set<PromptName>([
  'Menopause - Executive Report',
  'Peak Performance - Executive Report',
]);

const GenerateReportModal: React.FC<GenerateReportModalProps> = ({ patient, onClose }) => {
  const dispatch = useAppDispatch();
  const [assistantId] = useState(() => `report-generator-${uuidV4()}`);
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('');
  const [contentType, setContentType] = useState<'markdown' | 'html'>('markdown');
  const [reportCss, setReportCss] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const iframeReady = useRef(false);

  const { runSavedPromptTyped, streamingText, loadingReply, stopRequest } = useAssistant(
    assistantId,
    false,
    false,
  );

  const isCreatingReport = useIsLoading(api.HealthRecords.createReportDocument);

  const [reportVariant, setReportVariant] = useState<ReportVariant | null>(null);

  // Fetch CSS for the active variant; refetches when the user picks a different report type.
  useEffect(() => {
    let cancelled = false;
    fetchReportCss(reportVariant)
      .then((css) => {
        if (!cancelled) setReportCss(css);
      })
      .catch(() => {
        if (!cancelled) setReportCss('');
      });
    return () => {
      cancelled = true;
    };
  }, [reportVariant]);

  // Update content with streaming text while generating
  useEffect(() => {
    if (streamingText && loadingReply) {
      setContent(streamingText);
    }
  }, [streamingText, loadingReply]);

  // Ref callback: initialize iframe document shell once when mounted
  const iframeRefCallback = useCallback(
    (el: HTMLIFrameElement | null) => {
      iframeRef.current = el;
      if (!el || iframeReady.current) return;
      const doc = el.contentDocument;
      if (!doc) return;
      doc.open();
      doc.write(`<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700&family=Noto+Color+Emoji&display=swap" rel="stylesheet">
<style>${reportCss || ''}</style>
</head>
<body></body>
</html>`);
      doc.close();
      iframeReady.current = true;
    },
    [reportCss],
  );

  // Keep the iframe's <style> in sync with reportCss after mount. Without this,
  // an initial iframe mount can race the CSS fetch and end up with stale styles.
  useEffect(() => {
    if (contentType !== 'html' || !iframeReady.current) return;
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    const styleEl = doc.head.querySelector('style');
    if (styleEl && styleEl.textContent !== reportCss) {
      styleEl.textContent = reportCss || '';
    }
  }, [reportCss, contentType]);

  // Update iframe body content without reloading the whole document
  useEffect(() => {
    if (contentType !== 'html') return;
    const iframe = iframeRef.current;
    if (!iframe || !iframeReady.current) return;
    const body = iframe.contentDocument?.body;
    if (!body) return;

    if (loadingReply) {
      // While streaming: update content and keep read-only
      body.contentEditable = 'false';
      const currentHtml = streamingText || content;
      body.innerHTML = currentHtml || '<p style="color:#999">Generating...</p>';
      const el = iframe.contentDocument!.documentElement;
      el.scrollTop = el.scrollHeight;
    } else if (content) {
      // Generation finished: make editable
      if (body.contentEditable !== 'true') {
        body.contentEditable = 'true';
        body.style.cursor = 'text';

        // Add editing styles
        const style = iframe.contentDocument!.createElement('style');
        style.textContent = `
          [contenteditable]:focus { outline: none; }
          section:hover, [class*="box"]:hover, [class*="card"]:hover {
            outline: 1px dashed rgba(0,0,0,0.2);
          }
        `;
        iframe.contentDocument!.head.appendChild(style);
      }
    }
  }, [contentType, streamingText, content, loadingReply]);

  // Handle prompt execution
  const handleRunPrompt = useCallback(
    async (promptName: PromptName) => {
      setContent('');
      setTitle(promptName);
      const isHtml = HTML_REPORT_PROMPTS.has(promptName);
      const newContentType = isHtml ? 'html' : 'markdown';
      const newVariant = isHtml ? variantFromPromptName(promptName) : null;
      const variantChanged = newVariant !== reportVariant;
      if (newContentType !== contentType || variantChanged) {
        iframeReady.current = false;
      } else if (newContentType === 'html' && iframeRef.current?.contentDocument?.body) {
        // Clear iframe body for fresh re-run of same content type
        iframeRef.current.contentDocument.body.innerHTML =
          '<p style="color:#999">Generating...</p>';
      }
      setContentType(newContentType);
      setReportVariant(newVariant);

      try {
        const result = await runSavedPromptTyped(
          promptName,
          {} as any,
          {
            patientId: patient.id,
          } as any,
        );

        if (result && typeof result === 'string') {
          console.log(
            `[Report] HTTP result length=${result.length}, streaming length=${streamingText?.length}`,
          );
          setContent(result);
        }
      } catch (error) {
        console.error('Failed to generate report:', error);
        MiscUtils.showToastrError('Report Generation', 'Failed to generate report content');
      }
    },
    [runSavedPromptTyped, patient.id, contentType, reportVariant],
  );

  // Get the current HTML content from the iframe (may have been edited)
  const getEditedHtmlContent = useCallback((): string => {
    if (contentType === 'html' && iframeRef.current?.contentDocument?.body) {
      return iframeRef.current.contentDocument.body.innerHTML;
    }
    return content;
  }, [contentType, content]);

  // Handle creating the PDF report
  const handleCreateReport = useCallback(async () => {
    if (!title.trim()) {
      MiscUtils.showToastrError('Report Title Required', 'Please enter a title for the report');
      return;
    }

    const finalContent = getEditedHtmlContent();

    if (!finalContent.trim()) {
      MiscUtils.showToastrError(
        'Report Content Required',
        'Please enter or generate content for the report',
      );
      return;
    }

    const result = await dispatch(
      api.HealthRecords.createReportDocument({
        createReportDocumentPayload: {
          patient_id: patient.id!,
          title: title.trim(),
          content: finalContent.trim(),
          content_type: contentType,
        },
      }),
      {
        errorTitle: 'Create Report',
        successText: 'Report created successfully',
      },
    );

    if (result) {
      onClose();
    }
  }, [dispatch, patient.id, title, getEditedHtmlContent, contentType, onClose]);

  const currentContent = streamingText || content;
  const isGenerating = loadingReply;
  const isHtml = contentType === 'html';
  const canCreateReport =
    title.trim() && currentContent.trim() && !isGenerating && !isCreatingReport;

  return (
    <DefaultModal
      title="Generate Report"
      className={`generate-report-modal fit-modal${isHtml ? ' generate-report-modal--html' : ''}`}
      padding={true}
      wrap={true}
      draggable={false}
      isOpen={true}
      onRequestClose={onClose}
      content={
        <div className="generate-report-content">
          <div className="report-prompts-section">
            <span className="small-title">Quick Generate</span>
            <div className="prompt-chips">
              {REPORT_PROMPTS.map((prompt) => (
                <DefaultChip
                  key={prompt}
                  text={prompt}
                  onClick={() => handleRunPrompt(prompt)}
                  disabled={isGenerating}
                  backgroundColor="#42bcdd"
                  textColor="white"
                  alt={prompt}
                  style={{ marginRight: '8px', marginBottom: '8px' }}
                />
              ))}
            </div>
            {isGenerating && (
              <div className="stop-button-row">
                <DefaultButton
                  name="Stop"
                  icon="circle-stop"
                  onClick={stopRequest}
                  className="red"
                />
              </div>
            )}
          </div>

          <div className="report-form-section">
            <div className="row mb-3">
              <DefaultTextField
                label="Report Title"
                value={title}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value)}
                placeholder="Enter report title..."
                style={{ width: '100%' }}
                disabled={isGenerating}
              />
            </div>

            <div className="row">
              <div className="column full-width">
                <label className="small-title">
                  Report Content
                  {isGenerating && (
                    <span className="streaming-indicator">
                      <Icon src="spinner" className="fa-spin" height="12px" />
                      <span style={{ marginLeft: '5px' }}>Generating...</span>
                    </span>
                  )}
                </label>
                {isHtml ? (
                  <>
                    {!isGenerating && content && (
                      <div className="html-edit-hint">
                        <Icon src="circle-info" height="12px" />
                        <span>
                          Click on the report to edit text. Select content and press Delete to
                          remove sections.
                        </span>
                      </div>
                    )}
                    <iframe
                      key={reportVariant ?? 'default'}
                      ref={iframeRefCallback}
                      className="html-report-iframe"
                      sandbox="allow-same-origin"
                      title="Report Preview"
                    />
                  </>
                ) : isGenerating ? (
                  <div className="streaming-preview">
                    <pre>{currentContent || 'Generating...'}</pre>
                  </div>
                ) : (
                  <Suspense fallback={<div className="editor-loading">Loading editor...</div>}>
                    <RichTextEditable
                      value={content}
                      onChange={setContent}
                      placeholder="Type your report content here or use one of the quick generate options above..."
                      className="report-editor"
                    />
                  </Suspense>
                )}
              </div>
            </div>
          </div>

          <div className="report-actions-section">
            <DefaultButton
              name="Create PDF Report"
              icon="file-pdf"
              onClick={handleCreateReport}
              disabled={!canCreateReport}
              loading={isCreatingReport}
              className="blue"
              style={{ marginRight: '10px', width: '200px' }}
            />
          </div>
        </div>
      }
    />
  );
};

export default GenerateReportModal;
